import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Menu,
  MenuItem,
  TextField,
  InputAdornment
} from "@mui/material";
import { Icon } from "@iconify/react";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import NotInterestedIcon from "@mui/icons-material/NotInterested";
import CheckIcon from "@mui/icons-material/Check";
import { toast } from "sonner"; // you already use this elsewhere
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import AddWebhookDialog from "../../components/AddWebhook";
import {
  getWebhooks,
  createWebhook,
  updateWebhook,
  deleteWebhook,
  getFtpCenters,
  getOrganizationFtpSettings,
  revealFtpPassword,
  updateFtpSettings,
  syncSpandanReports,
} from "../../api/settings";
import ChangeFtpPasswordDialog from "../../components/ChangeFtpPasswordDialog";
import SearchIcon from "../../assets/icons/searchIcon.svg";
import { AuthContext } from "../../context/AuthContextProvider";

const DeveloperSettings = () => {
  const { user } = useContext(AuthContext);
  // userType stays plain "b2b" for migrated businesses too - migration is a
  // separate flag on the Business doc.
  const isMigratedUser = user?.userType === "b2b" && user?.migration === true;
  const [reportSyncLoading, setReportSyncLoading] = useState(false);

  const handleSyncReports = async () => {
    try {
      setReportSyncLoading(true);
      const response = await syncSpandanReports();
      if (response?.success) {
        toast.success(response.message || "Reports synced successfully");
      } else {
        toast.error(response?.message || "Couldn't sync your reports right now");
      }
    } catch (err) {
      toast.error(err.message || "Couldn't sync your reports right now");
    } finally {
      setReportSyncLoading(false);
    }
  };

  const [openDialog, setOpenDialog] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedWebhook, setSelectedWebhook] = useState(null);
  const [editingWebhook, setEditingWebhook] = useState(null);
  const [webhooks, setWebhooks] = useState([]);
  const [ftpData, setFtpData] = useState({});
  const [centers, setCenters] = useState([]);
  const [search, setSearch] = useState("");
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [selectedApiKey, setSelectedApiKey] = useState(null);
  const [loadingCenters, setLoadingCenters] = useState(false);
  const [showOrgPassword, setShowOrgPassword] = useState(false);
  const [orgPassword, setOrgPassword] = useState(null);

  const getBusinessId = () => {
    try {
      const auth = JSON.parse(localStorage.getItem("@auth_login"));
      return auth?.data?.businessId || null;
    } catch (e) {
      return null;
    }
  };


  /* ===================== FETCH ===================== */
  const fetchWebhooks = async () => {
    try {
      const res = await getWebhooks();
      const list = Array.isArray(res?.data) ? res.data : [];

      const normalized = list.map((item) => {
        const events = {};

        if (item.event === "REPORT_INTERPRETED") {
          events.REPORT_INTERPRETED =
            item.to_send && Object.keys(item.to_send).length
              ? {
                pdf: !!item.to_send.pdf,
                data: !!item.to_send.data,
              }
              : true;
        }

        if (item.event === "AI_GENERATED_REPORT") {
          events.AI_GENERATED_REPORT =
            item.to_send && Object.keys(item.to_send).length
              ? {
                pdf: !!item.to_send.pdf,
                data: !!item.to_send.data,
              }
              : true;
        }

        return {
          _id: item._id,
          url: item.url,
          status: item.status,
          events, //  IMPORTANT
          updatedAt: new Date(item.updatedAt).toLocaleString(),
        };
      });

      setWebhooks(normalized);
    } catch (err) {
      console.error(err);
      setWebhooks([]);
    }
  };
  const fetchFtpSettings = async () => {
    try {
      const res = await getOrganizationFtpSettings();
      setFtpData(res?.data || {});
    } catch (err) {
      console.error(err);
    }
  };

  const fetchCenters = async (query = "") => {
    try {
      setLoadingCenters(true);
      const res = await getFtpCenters({ search: query });
      setCenters(res?.data || []);
    } catch (err) {
      console.error(err);
      setCenters([]);
    } finally {
      setLoadingCenters(false);
    }
  };
  useEffect(() => {
    fetchWebhooks();
    fetchFtpSettings();
    fetchCenters();
  }, []);

  /* ===================== FTP ACTIONS ===================== */

  const handleReveal = async (apiKey) => {
    try {
      const res = await revealFtpPassword(apiKey);

      const password = res?.data?.ftpPassword;
      const isLegacy = res?.data?.isLegacy;

      const displayPassword =
        isLegacy || !password
          ? "⚠ Reset required to view"
          : password;

      setVisiblePasswords((prev) => ({
        ...prev,
        [apiKey]: displayPassword,
      }));
    } catch (err) {
      toast.error("Failed to fetch password");
    }
  };
  const handleUpdatePassword = async ({ apiKey, password }) => {
    try {
      await updateFtpSettings(apiKey, { password });
      toast.success("Password updated successfully");
      setVisiblePasswords((prev) => {
        const copy = { ...prev };
        delete copy[apiKey];
        return copy;
      });
      setShowOrgPassword(false);
      setOrgPassword(null);
      fetchCenters();
    } catch (err) {
      toast.error("Failed to update password");
    }
  };

  /* ===================== MENU ===================== */
  const handleMenuOpen = (event, webhook) => {
    setAnchorEl(event.currentTarget);
    setSelectedWebhook(webhook);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    setEditingWebhook(selectedWebhook);
    setOpenDialog(true);
    handleMenuClose();
  };

  const handleToggleStatus = async () => {
    if (!selectedWebhook) return;

    const isActive = selectedWebhook.status === "active";

    try {
      const res = await updateWebhook({
        id: selectedWebhook._id,
        url: selectedWebhook.url,
        events: selectedWebhook.events,
        status: isActive ? "disabled" : "active",
        signature: { enabled: false },
      });

      toast.success(`${res.message}`);

      fetchWebhooks();
    } catch (err) {
      console.error(err);

      toast.error(`${err.message}`);
    } finally {
      setSelectedWebhook(null);
      handleMenuClose();
    }
  };

  useEffect(() => {
    const delay = setTimeout(() => {
      fetchCenters(search);
    }, 400);

    return () => clearTimeout(delay);
  }, [search]);


  const handleRevealOrgPassword = async () => {
    try {
      if (showOrgPassword) {
        setShowOrgPassword(false);
        return;
      }
      const businessId = getBusinessId();
      if (!businessId) {
        toast.error("Business ID not found");
        return;
      }
      const res = await revealFtpPassword(businessId);
      const password = res?.data?.ftpPassword;
      const isLegacy = res?.data?.isLegacy;
      setOrgPassword(isLegacy ? "⚠ Reset required to view" : (password || "—"));
      setShowOrgPassword(true);
    } catch (err) {
      toast.error("Failed to fetch org password");
    }
  };

  const handleDelete = async () => {
    if (!selectedWebhook) return;

    try {
      const id =
        typeof selectedWebhook._id === "string"
          ? selectedWebhook._id
          : selectedWebhook._id?.toString?.();

      if (!id) {
        console.error("Invalid webhook id", selectedWebhook._id);
        return;
      }

      await deleteWebhook(id);
      fetchWebhooks();
    } catch (err) {
      console.error(err);
    }

    handleMenuClose();
  };

  /* ===================== SAVE ===================== */

  const handleSaveWebhook = async (payload) => {
    try {
      const isEdit = Boolean(editingWebhook);

      const res = isEdit
        ? await updateWebhook({
          ...payload,
          id: editingWebhook._id,
        })
        : await createWebhook(payload);

      if (res?.failed?.length) {
        res.failed.forEach((f) => {
          toast.error(`${f.event.replaceAll("_", " ")}: ${f.error}`);
        });
      }

      if (res?.success?.length) {
        toast.success(
          isEdit
            ? "Webhook updated successfully"
            : "Webhook created successfully",
        );
      }

      if (res?.failed?.length && !res?.success?.length) {
        return res;
      }

      fetchWebhooks();
      setEditingWebhook(null);
      setOpenDialog(false);

      return res;
    } catch (err) {
      toast.error("Something went wrong while saving webhook");
      return null;
    }
  };

  return (
    <Box >
      {isMigratedUser && (
        <Paper
          elevation={0}
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            gap: 2,
            p: 3,
            mb: 3,
            borderRadius: "12px",
            backgroundColor: "var(--app-surface)",
            fontFamily: "Figtree",
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, fontSize: "18px", fontFamily: "Figtree" }}
            >
              Sync Spandan Reports
            </Typography>
            <Typography
              sx={{ fontSize: "14px", color: "var(--app-text-muted)", mt: 0.5 }}
            >
              Bring over ECG reports from your old Spandan account into Spandan One.
            </Typography>
          </Box>

          <Button
            variant="contained"
            disabled={reportSyncLoading}
            onClick={handleSyncReports}
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              backgroundColor: "#5751E9",
              fontWeight: 600,
              boxShadow: "none",
              fontFamily: "Figtree",
              width: { xs: "100%", sm: "auto" },
              height: "45px",
              px: 3,
              flexShrink: 0,
            }}
          >
            {reportSyncLoading ? "Syncing..." : "Sync Reports"}
          </Button>
        </Paper>
      )}

      {/* HEADER */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" }, // 📱 stack → 💻 row
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: 2,
          mb: 2,
        }}
      >
        <Typography
          variant="h6"
          sx={{ fontWeight: 700, fontSize: "20px", fontFamily: "Figtree" }}
        >
          Webhook Integrations
        </Typography>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditingWebhook(null);
            setOpenDialog(true);
          }}
          fullWidth={{ xs: true, sm: false }} // 👈 ignored by MUI, so we control via sx below
          sx={{
            textTransform: "none",
            borderRadius: "10px",
            backgroundColor: "#5751E9",
            fontWeight: 600,
            boxShadow: "none",
            fontFamily: "Figtree",

            width: { xs: "100%", sm: "187px" }, // 📱 full width button
            height: "45px",
          }}
        >
          Add New Webhook
        </Button>
      </Box>

      {/* TABLE */}
      {!webhooks.length ? (
        <>
          <Paper
            elevation={0}
            sx={{
              py: 6,
              textAlign: "center",
              borderRadius: "12px",
              fontFamily: "Figtree",
            }}
          >
            <Typography fontSize={16} fontWeight={600} color="var(--app-text-muted)">
              No webhooks found
            </Typography>
            <Typography fontSize={14} color="var(--app-text-muted)" mt={1}>
              You haven’t added any webhook integrations yet.
            </Typography>
          </Paper>
        </>
      ) : (
        <>
          <TableContainer className="app-table-card"
            component={Paper}
            elevation={0}
            sx={{ background: "transparent" }}
          >
            <Table
              sx={{
                backgroundColor: "var(--app-surface-2)",
                borderCollapse: "separate",
                borderSpacing: "0 10px",
              }}
            >
              <TableHead>
                <TableRow sx={{ fontFamily: "Figtree" }}>
                  <TableCell sx={headStyle}>URL</TableCell>
                  <TableCell sx={headStyle}>STATUS</TableCell>
                  <TableCell sx={headStyle}>EVENTS</TableCell>
                  <TableCell sx={headStyle}>LAST UPDATED</TableCell>
                  <TableCell sx={headStyle} />
                </TableRow>
              </TableHead>

              <TableBody>
                {webhooks?.map((item) => (
                  <TableRow
                    key={item._id}
                    sx={{
                      "& td": {
                        backgroundColor: "var(--app-surface)",
                        borderBottom: "none",
                        py: 1.5,
                      },
                      "& td:first-of-type": {
                        borderTopLeftRadius: "12px",
                        borderBottomLeftRadius: "12px",
                      },
                      "& td:last-of-type": {
                        borderTopRightRadius: "12px",
                        borderBottomRightRadius: "12px",
                      },
                      "&:hover td": {
                        backgroundColor: "var(--app-surface-2)",
                      },
                      fontFamily: "Figtree",
                    }}
                  >
                    <TableCell>
                      <Typography
                        sx={{
                          color: "#4F46E5",
                          fontWeight: 600,
                          textDecoration: "underline",
                        }}
                      >
                        {item.url}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={
                          item.status === "active" ? "Enabled" : "Disabled"
                        }
                        size="small"
                        sx={{
                          borderRadius: "4px",
                          backgroundColor:
                            item.status === "active" ? "#E6F4EA" : "#FDECEC",
                          color:
                            item.status === "active" ? "#2E7D32" : "#C62828",
                          fontWeight: 600,
                          fontFamily: "Figtree",
                        }}
                      />
                    </TableCell>

                    <TableCell>
                      {Object.values(item.events || {}).filter(Boolean).length}{" "}
                      events
                    </TableCell>

                    <TableCell>
                      <Typography fontSize={14} color="var(--app-text-muted)">
                        {item.updatedAt}
                      </Typography>
                    </TableCell>

                    <TableCell align="right">
                      <IconButton onClick={(e) => handleMenuOpen(e, item)}>
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* MENU */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon sx={{ fontSize: 16, mr: 1 }} /> Edit
        </MenuItem>

        <MenuItem onClick={handleToggleStatus}>
          {selectedWebhook?.status === "active" ? (
            <>
              <NotInterestedIcon sx={{ fontSize: 16, mr: 1 }} />
              Disable
            </>
          ) : (
            <>
              <CheckIcon sx={{ fontSize: 16, mr: 1 }} />
              Enable
            </>
          )}
        </MenuItem>

        <MenuItem onClick={handleDelete}>
          <DeleteForeverIcon sx={{ fontSize: 18, mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* DIALOG */}
      <AddWebhookDialog
        open={openDialog}
        onClose={() => {
          setEditingWebhook(null);
          setOpenDialog(false);
        }}
        initialData={editingWebhook}
        onSubmit={handleSaveWebhook}
      />
      {ftpData?.ftpStatus && (
        <>
          <Box mt={4}>
            <Typography
              variant="h6"
              fontWeight={600}
              sx={{ mb: 2, fontFamily: "Figtree" }}
            >
              Device Integration (FTP)
            </Typography>

            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: "12px",
                backgroundColor: "var(--app-surface)",
                fontFamily: "Figtree",
              }}
            >
              <Box mb={2}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography fontSize={14} fontWeight={500}>
                    IP Address : {ftpData?.ipAddress}
                  </Typography>
                  {ftpData?.ipAddress && (
                    <IconButton
                      size="small"
                      onClick={() => {
                        navigator.clipboard.writeText(ftpData.ipAddress);
                        toast.success("IP address copied");
                      }}
                    >
                      <Icon icon="mdi:content-copy" width={16} height={16} />
                    </IconButton>
                  )}
                </Box>

                <Box display="flex" alignItems="center" gap={1} mt={1}>
                  <Typography fontSize={14} fontWeight={500}>
                    Default Password :{" "}
                    {showOrgPassword
                      ? orgPassword || "----"
                      : "**********"}
                  </Typography>

                  <IconButton size="small" onClick={handleRevealOrgPassword}>
                    {showOrgPassword ? (
                      <VisibilityOffIcon fontSize="small" />
                    ) : (
                      <VisibilityIcon fontSize="small" />
                    )}
                  </IconButton>
                </Box>
              </Box>

              <Typography
                fontSize={14}
                fontWeight={600}
                sx={{ mb: 1.5 }}
              >
                Centre List
              </Typography>

              <Box mb={2}>
                <TextField
                  fullWidth
                  placeholder="Community Health Centre"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <img
                          src={SearchIcon}
                          alt="search"
                          style={{
                            width: 16,
                            height: 16,
                            opacity: 0.6,
                            display: "block",
                          }}
                        />
                      </InputAdornment>
                    ),
                    sx: {
                      height: "44px",
                      borderRadius: "10px",
                      backgroundColor: "var(--app-surface)",
                      fontSize: "14px",
                    },
                  }}
                />
              </Box>

              {loadingCenters ? (
                <Box textAlign="center" py={3}>
                  <Typography fontSize={14} color="var(--app-text-muted)">
                    Loading centers...
                  </Typography>
                </Box>
              ) : centers.length === 0 ? (
                <Box textAlign="center" py={3}>
                  <Typography fontSize={14} color="var(--app-text-muted)">
                    No centers found
                  </Typography>
                </Box>
              ) : (
                centers.map((center) => (
                  <Box
                    key={center.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      px: 2,
                      py: 1.5,
                      mb: 1,
                      borderRadius: "10px",
                      backgroundColor: "var(--app-surface-2)",
                    }}
                  >
                    <Typography fontWeight={500}>{center.name}</Typography>

                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      sx={{ color: "var(--app-text-muted)", fontSize: "14px" }}
                    >
                      <span>ID : {center.id}</span>
                      <span>|</span>
                      <span>
                        Password : {visiblePasswords[center.id] || "**********"}
                      </span>

                      <IconButton
                        size="small"
                        onClick={() =>
                          visiblePasswords[center.id]
                            ? setVisiblePasswords((prev) => {
                              const copy = { ...prev };
                              delete copy[center.id];
                              return copy;
                            })
                            : handleReveal(center.id)
                        }
                      >
                        {visiblePasswords[center.id] ? (
                          <VisibilityOffIcon fontSize="small" />
                        ) : (
                          <VisibilityIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Box>

                    <Typography
                      sx={{
                        color: "#4F46E5",
                        fontWeight: 600,
                        fontSize: "14px",
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        setSelectedApiKey(center.id);
                        setOpenPasswordDialog(true);
                      }}
                    >
                      Change Password
                    </Typography>
                  </Box>
                ))
              )}
            </Paper>
          </Box>

          <ChangeFtpPasswordDialog
            open={openPasswordDialog}
            onClose={() => setOpenPasswordDialog(false)}
            apiKey={selectedApiKey}
            onSave={handleUpdatePassword}
          />
        </>
      )}


    </Box>
  );
};

const headStyle = {
  fontSize: "15px",
  fontWeight: 800,
  color: "#A3A3A3",
  borderBottom: "none",
  py: 1.5,
  fontFamily: "Figtree",
};

export default DeveloperSettings;
